// src/pages/FamilyProfile.jsx
import React, { useState } from "react";
import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Paper,
  Avatar,
  CircularProgress,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { PhotoCamera, Save, Delete } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import { Navigate, useNavigate } from "react-router-dom";
import { EditUserFormData, getOneUser } from "../redux/slices/user/thunk";
import imageCompression from "browser-image-compression";
import { useDispatch } from "react-redux";

const FamilyProfile = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("userForm.validation.nameRequired")),
    email: Yup.string()
      .email(t("validation.invalidEmail"))
      .required(t("validation.emailRequired")),
    phone: Yup.string().required(t("userForm.validation.phoneRequired")),

    // .matches(/^[0-9]{6,}$/, t("userForm.validation.phoneInvalid")),
    password: Yup.string()
      .min(6, t("userForm.validation.passwordMin"))
      .notRequired(),
    address: Yup.string().required(t("userForm.validation.addressRequired")),
    locationLink: Yup.string().url(
      t("userForm.validation.locationLinkInvalid")
    ),
    // .required(t("userForm.validation.locationLinkRequired")),
    description: Yup.string(),
    activityDescription: Yup.string(),
    bankAccount: Yup.string(),
  });

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      name: user.userName,
      phone: user.phoneNumber,
      email: user.email,
      address: user.address,
      locationLink: user.location,
      photoUrl: user.image,
      description: user.description,
      activityDescription: user.activityDescription,
      bankAccount: user.bankAccountNumber,
      password: "",
    },
    validationSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true,
  });

  // Handle photo change
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        formik.setFieldValue("photoUrl", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle photo removal
  const handleRemovePhoto = () => {
    setPhotoFile(null);
    formik.setFieldValue("photoUrl", "");
  };

  // Handle form submission

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      // Compress image if exists
      let compressedFile = null;
      if (photoFile) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };

        try {
          compressedFile = await imageCompression(photoFile, options);
        } catch (compressionError) {
          console.error("Image compression failed:", compressionError);
          compressedFile = photoFile; // Fallback to original
        }
      }

      // Prepare form data with proper field mapping
      const formData = new FormData();

      // if (values.password) {
      //   formData.append("password", values.password);
      // }

      // Map frontend fields to backend keys
      const fieldMap = {
        name: "userName",
        email: "email",
        phone: "phoneNumber",
        address: "address",
        locationLink: "location",
        description: "description",
        bankAccount: "bankAccountNumber",
        activityDescription: "activityDescription", // Add if needed by backend
      };

      // Append mapped fields
      Object.entries(fieldMap).forEach(([frontendKey, backendKey]) => {
        if (values[frontendKey] !== undefined && values[frontendKey] !== null) {
          // Special handling for phone number
          if (frontendKey === "phone") {
            const cleaned = values.phone.replace(/\D/g, "");
            formData.append(backendKey, `${cleaned}`);
          } else {
            formData.append(backendKey, values[frontendKey]);
          }
        }
      });

      // Only append password if provided
      if (values.password) {
        formData.append("password", values.password);
      }

      // Append country code separately
      formData.append("countryCode", "966");

      // Append compressed image
      if (compressedFile) {
        formData.append("image", compressedFile);
      }

      await dispatch(EditUserFormData({ id: user?._id, formData }));
      const response = await dispatch(getOneUser({ id: user?._id }));
      console.log("response", response?.payload);
      localStorage.setItem("user", JSON.stringify(response?.payload));
    } catch (error) {
      console.error("Form submission error:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (user?.type !== "family") {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box mb={3} textAlign="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {t("familyProfile.title")}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {t("familyProfile.subtitle")}
          </Typography>
        </Box>

        {/* Photo Upload Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            position: "relative",
          }}
        >
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="photo-upload"
            type="file"
            onChange={handlePhotoChange}
          />

          {formik.values.photoUrl ? (
            <>
              <Avatar
                src={formik.values.photoUrl}
                sx={{
                  width: 120,
                  height: 120,
                  border: `3px solid ${theme.palette.primary.main}`,
                  mb: 2,
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    color="primary"
                    component="span"
                    startIcon={<PhotoCamera sx={{ ml: 2 }} />}
                  >
                    {t("userForm.photo.change")}
                  </Button>
                </label>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete sx={{ ml: 2 }} />}
                  onClick={handleRemovePhoto}
                >
                  {t("userForm.photo.remove")}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  bgcolor: theme.palette.grey[200],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <PhotoCamera
                  sx={{ fontSize: 40, color: theme.palette.grey[500] }}
                />
              </Box>
              <Typography variant="body1" color="textSecondary" mb={2}>
                {t("userForm.photo.optional")}
              </Typography>
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<PhotoCamera sx={{ ml: 2 }} />}
                >
                  {t("userForm.photo.upload")}
                </Button>
              </label>
            </>
          )}
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Mandatory Fields */}
            <Grid item xs={12} md={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.fullName")}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                isRtl={isArabic}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.email")}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
                isRtl={isArabic}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.phoneNumber")}
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                required
                isRtl={isArabic}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+966</InputAdornment>
                  ),
                }}
              />
            </Grid>

<Grid item xs={12}>
  <CustomTextField
    fullWidth
    label={t("userForm.fields.password")}
    name="password"
    type="password"
    value={formik.values.password}
    onChange={formik.handleChange}
    onBlur={formik.handleBlur}
    isRtl={isArabic}
    error={formik.touched.password && Boolean(formik.errors.password)}
    helperText={
      formik.touched.password && formik.errors.password
        ? formik.errors.password
        : t("userForm.messages.leavePasswordBlank")
    }
    placeholder={t("userForm.messages.leavePasswordBlank")}
  />
</Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.address")}
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                isRtl={isArabic}
                required
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.locationLink")}
                name="locationLink"
                value={formik.values.locationLink}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isRtl={isArabic}
                error={
                  formik.touched.locationLink &&
                  Boolean(formik.errors.locationLink)
                }
                helperText={
                  formik.touched.locationLink && formik.errors.locationLink
                }
                // required
                placeholder="https://maps.app.goo.gl/..."
              />
            </Grid>

            {/* Optional Fields */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.description")}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                helperText={t("userForm.photo.optional")}
                multiline
                isRtl={isArabic}
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.activityDescription")}
                name="activityDescription"
                value={formik.values.activityDescription}
                onChange={formik.handleChange}
                helperText={t("userForm.photo.optional")}
                multiline
                isRtl={isArabic}
                rows={3}
                placeholder={t("familyProfile.activityPlaceholder")}
              />
            </Grid>

            <Grid item xs={12} md={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.bankAccount")}
                name="bankAccount"
                value={formik.values.bankAccount}
                onChange={formik.handleChange}
                helperText={t("userForm.photo.optional")}
                isRtl={isArabic}
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save sx={{ ml: 2 }} />
                    )
                  }
                  disabled={loading}
                  sx={{ px: 6, py: 1.5, minWidth: 200 }}
                >
                  {t("familyProfile.buttons.saveChanges")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default FamilyProfile;
